import { Component, OnInit, Input } from '@angular/core';
import * as _ from 'lodash';
import { GfTitleService } from '../../../../services/core.title.service';
import { ProjectService } from '../../../../services/project.service';
import { ProjectModel } from '../../../../services/project.model.service';
import { UiService } from '../../../../services/core.ui.service';
import { StateService } from '@uirouter/angular';

@Component({
  selector: 'project-admin-roles',
  template: require('./project.admin.roles.html')
})
export class ProjectAdminRolesComponent implements OnInit {
  @Input() project: any;
  @Input() roles: any;
  @Input() isProjectAdmin: any;

  role: any = false;
  roleSettings: any = false;
  deleteRole: any = false;
  deletingRole = false;
  templateRoleSettings: any = false;
  isLoading = false;
  savingRole = false;

  accessLevels: any;
  trackerAccessLevels: any = {};

  unixName: string;
  roleId: any;

  constructor(
    protected stateService: StateService, 
    
    private titleService: GfTitleService,
    private projectService: ProjectService,
    private projectModel: ProjectModel,
    private uiService: UiService
  ) {}

  ngOnInit(): void {
    this.unixName = this.stateService.params['unixName'];
    this.roleId = +this.stateService.params['roleId'];

    // Initialize roles collection and ensure each role has the required properties
    this.roles = _.get(this.roles, 'items', []).map(role => ({
      ...role,
      rel: role.rel || { user_count: 0 }
    }));
    
    this.role = false;
    this.roleSettings = false;
    this.deleteRole = false;
    this.deletingRole = false;
    this.templateRoleSettings = false;
    this.isLoading = false;

    this.accessLevels = this.projectModel.accessLevels;
    this.trackerAccessLevels = {};

    this.titleService.setTitle(this.project.name, 'Admin', 'Roles');

    // If a valid roleId exists (> 0), load its settings; if roleId === 0, create a new role
    if (this.roleId > 0) {
      this.editRole();
    } else if (this.roleId === 0) {
      this.editNewRole();
    }
  }

  deleteTitle(r: any): string {
    return this.deleteRole === r
      ? "Are you sure?  Click again to delete."
      : "This role has no members.  Click to delete";
  }

  deleteButtonClasses(r: any): string {
    return this.deleteRole === r ? "btn-danger" : "btn-trans";
  }

  deleteButtonPageClasses(r: any): string {
    return this.deleteRole === r ? "btn-danger" : "btn-warning";
  }

  deleteGlyphClasses(r: any): string {
    if (this.deleteRole !== r) {
      return 'glyphicon-remove';
    }
    if (this.deletingRole) {
      return 'glyphicon-refresh el-pulse';
    }
    return 'glyphicon-question-sign';
  }

  doDelete(role: any): void {
    if (this.deleteRole !== role) {
      this.deleteRole = role;
      return;
    }
    this.deletingRole = true;
    this.projectService.deleteRole(this.unixName, this.deleteRole.id)
      .then(() => {
        this.uiService.message.showSuccess('Deleted ' + this.deleteRole.roleName + ' successfully.');
        this.reloadState();
      })
      .catch(() => {
        this.uiService.message.showSuccess('Could not delete ' + this.deleteRole.roleName);
      })
      .finally(() => {
        this.role = false;
        this.deleteRole = false;
        this.deletingRole = false;
      });
  }

  editRole(): void {
    this.role = false;
    this.deleteRole = false;
    const role: any = _.find(this.roles, (r: any) => r.id === this.roleId);
    if (!role) { return; }
    this.isLoading = true;
    this.projectService.getRoleSettings(this.unixName, this.roleId)
      .then((data: any) => {
        this.role = role;
        this.roleSettings = this.organizeRoleSettings(
          _.get(data, 'rel.role_settings', this.templateRoleSettings)
        );
      })
      .catch(() => {
        this.uiService.message.showWarning("Sorry, could not load setting for " + role.roleName);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  editNewRole(): void {
    if (!this.templateRoleSettings) {
      this.projectService.getRoleSettingsTemplate(this.unixName)
        .then((data: any) => {
          this.templateRoleSettings = data.items;
          this.role = {
            id: 0,
            roleName: 'New Role ' + (this.roles.length + 1),
            rel: { user_count: 0 } 
          };
          this.roleSettings = this.organizeRoleSettings(_.cloneDeep(this.templateRoleSettings));
        });
    } else {
      this.role = {
        id: 0,
        roleName: 'New Role ' + (this.roles.length + 1),
        rel: { user_count: 0 }
      };
      this.roleSettings = this.organizeRoleSettings(_.cloneDeep(this.templateRoleSettings));
    }
  }

  saveRole(): void {
    if (!this.role) { return; }
    this.savingRole = true;

    const role_settings = _.cloneDeep(this.roleSettings);
    role_settings.tracker.settings = _.map(role_settings.tracker.settings, 'value');

    const toSave = {
      id: this.role.id,
      roleName: this.role.roleName,
      rel: { role_settings }
    };

    this.projectService.saveRole(this.unixName, toSave)
      .then((result: any) => {
        this.uiService.message.showSuccess(this.role.roleName + ' saved successfully');
        
        // If creating a new role, navigate to the updated role
        if (toSave.id === 0) {
          // Reloads to the edit form with the new role id
          const newRoleId = result.id;
          this.stateService.go('.', { roleId: newRoleId }, { location: true, reload: true });
          this.role.id = newRoleId;
          // Refresh the roles list to include the new role
          this.loadRoles();
        } else {
          this.loadRoles();
        }
      })
      .catch((failure: any) => {
        if (_.get(failure, 'data.roleName', false)) {
          this.uiService.message.showWarning('Role Name is required, and must be unique within the project');
        } else {
          this.uiService.message.showWarning('Could not update the Role. An unspecified error occurred');
        }
      })
      .finally(() => {
        this.savingRole = false;
      });
  }

  loadRoles(): void {
    this.projectService.roles(this.unixName)
      .then((data: any) => {
        // Ensure each role has the required properties
        this.roles = (data.items || []).map(role => ({
          ...role,
          rel: role.rel || { user_count: 0 }
        }));
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  organizeRoleSettings(roleSettings: any): any {
    const mapped = _.mapValues(roleSettings, (v: any) => ({
      admin: this.adminSetting(v),
      settings: this.nonAdminSettings(v)
    }));
    this.trackerAccessLevels = this.projectModel.getTrackerAccessLevels(mapped.tracker.settings);
    mapped.tracker.settings = this.projectModel.getCurrentAccessLevels(this.trackerAccessLevels, mapped.tracker.settings);
    return mapped;
  }

  adminSetting(section: any): any {
    return _.find(section, { refId: 0 });
  }

  nonAdminSettings(section: any): any {
    return _.filter(section, (item: any) => item.refId > 0);
  }

  setRoleSetting(roleSetting: any, value: any): boolean {
    if (value === undefined || value === _.get(this.roleSettings, roleSetting)) { return false; }
    _.set(this.roleSettings, roleSetting, value);
    return true;
  }

  setSubitemAccessLevel(t: any, value: any): boolean {
    if (value === undefined || value === t?.value) { return false; }
    t.value = value;
    return true;
  }

  trackByRoleId(index: number, role: any): number {
    return role.id;
  }

  private reloadState(): void {
    this.stateService.go('.', this.stateService.params, { location: true, reload: true });
  }
}
